"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { searchIncotermLocations } from "~/actions/incotermLocationActions";
import { Icon, IconName } from "~/components/icons/Icon";
import { TextInput } from "~/components/inputs/TextInput";
import {
  INCOTERM_LOCATION_SEARCH_DEBOUNCE_MS,
  INCOTERM_LOCATION_SEARCH_MIN_CHARS,
} from "~/constants/incotermLocationConstants";
import { usePostHog } from "~/hooks/usePostHog";
import { useI18n } from "~/locales/client";
import {
  IncotermLocationOption,
  IncotermLocationsPage,
  IncotermLocationTrackingContext,
  SelectedIncotermLocation,
} from "~/types-and-enums/incotermLocationTypes";
import { buildIncotermLocationLabel, hasPostalCode, toSelectedIncotermLocation } from "~/utils/incotermLocationUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import IncotermLocationCreateForm, { IncotermLocationFormValues } from "./IncotermLocationCreateForm";
import IncotermLocationOptionContent from "./IncotermLocationOptionContent";

type IncotermLocationSelectProps = {
  name: string;
  value: SelectedIncotermLocation | null;
  onChange: (location: SelectedIncotermLocation | null) => void;
  trackingContext: IncotermLocationTrackingContext;
  placeholder?: string; // default translate("incoterm-location.search-placeholder")
  error?: boolean; // red border like other inputs
  isDisabled?: boolean;
  autoFocus?: boolean; // grid cell edit
  defaultOpen?: boolean; // grid cell edit: open popover on mount
  onRequestClose?: () => void; // fired when popover closes without a selection (grid exits edit mode)
  allowClear?: boolean; // default true
  className?: string;
};

type SelectionSource = "recent" | "search" | "duplicate_match";
type SearchResponse = Awaited<ReturnType<typeof searchIncotermLocations>>;

const POPOVER_MIN_WIDTH = 420;
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Searchable select for the supplier's Incoterm locations (TT-4239): server-side typeahead over existing locations
 * (recently used first), plus an inline form to create a new one with duplicate detection.
 * Typing never calls onChange — only selecting, creating or clearing does.
 */
const IncotermLocationSelect = ({
  name,
  value,
  onChange,
  trackingContext,
  placeholder,
  error,
  isDisabled,
  autoFocus,
  defaultOpen,
  onRequestClose,
  allowClear = true,
  className,
}: IncotermLocationSelectProps) => {
  const translate = useI18n();
  const { captureEvent } = usePostHog();
  const listboxId = `${useId()}-listbox`;

  const [initiallyOpen] = useState(() => Boolean(defaultOpen && !isDisabled));
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [mode, setMode] = useState<"search" | "create">("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IncotermLocationOption[]>([]);
  const [pageInfo, setPageInfo] = useState<IncotermLocationsPage["page"] | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">(initiallyOpen ? "loading" : "idle");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [recentCache, setRecentCache] = useState<IncotermLocationsPage | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [createPrefill, setCreatePrefill] = useState<Partial<IncotermLocationFormValues>>({});

  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popoverBodyRef = useRef<HTMLDivElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const isOpenRef = useRef(initiallyOpen);
  const requestSeqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipFocusOpenRef = useRef(false);
  const lastRequestRef = useRef<{ q: string; page: number }>({ q: "", page: 0 });

  const isRecentQuery = query.trim().length < INCOTERM_LOCATION_SEARCH_MIN_CHARS;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const clearDebounce = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
  };

  /** Resolves with the response, or null when a newer request (or closing the popover) superseded it. */
  const startSearch = useCallback(async (q: string, page: number): Promise<SearchResponse | null> => {
    const seq = ++requestSeqRef.current;
    lastRequestRef.current = { q, page };
    const response = await searchIncotermLocations({ ...(q ? { q } : {}), ...(page > 0 ? { page } : {}) });
    return seq === requestSeqRef.current ? response : null;
  }, []);

  const applySearchResponse = useCallback((response: SearchResponse | null, q: string, page: number) => {
    if (!response) return;
    setIsLoadingMore(false);
    if (!response.success || !response.data) {
      setLoadState("error");
      return;
    }
    const data = response.data;
    setLoadState("idle");
    setPageInfo(data.page);
    setResults((prev) => (page === 0 ? data.content : [...prev, ...data.content]));
    if (page === 0) setActiveIndex(-1);
    if (!q) {
      setRecentCache((prev) =>
        page === 0 || !prev ? data : { content: [...prev.content, ...data.content], page: data.page }
      );
    }
  }, []);

  const fetchPage = (q: string, page: number) => {
    if (page === 0) setLoadState("loading");
    else setIsLoadingMore(true);
    void startSearch(q, page).then((response) => applySearchResponse(response, q, page));
  };

  const showRecent = () => {
    clearDebounce();
    if (recentCache) {
      requestSeqRef.current += 1; // drop any in-flight search
      lastRequestRef.current = { q: "", page: recentCache.page.number };
      setResults(recentCache.content);
      setPageInfo(recentCache.page);
      setLoadState("idle");
      setActiveIndex(-1);
    } else {
      fetchPage("", 0);
    }
  };

  const openPopover = () => {
    if (isDisabled || isOpenRef.current) return;
    isOpenRef.current = true;
    setIsOpen(true);
    setMode("search");
    setQuery("");
    showRecent();
  };

  const closePopover = useCallback(
    (reason: "selection" | "dismiss") => {
      if (!isOpenRef.current) return;
      isOpenRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestSeqRef.current += 1; // ignore responses that arrive after closing
      setIsOpen(false);
      setMode("search");
      setQuery("");
      setActiveIndex(-1);
      setIsLoadingMore(false);
      setLoadState("idle");
      if (reason === "dismiss") onRequestClose?.();
    },
    [onRequestClose]
  );

  /** Moves focus back to the input without reopening the popover. */
  const focusInput = () => {
    const input = inputRef.current;
    if (!input || document.activeElement === input) return;
    skipFocusOpenRef.current = true;
    input.focus();
  };

  const selectOption = (option: IncotermLocationOption, source: SelectionSource) => {
    if (!hasPostalCode(option)) return;
    onChange(toSelectedIncotermLocation(option));
    captureEvent("incoterm_location_selected", {
      context: trackingContext,
      location_id: option.id,
      source,
      query_length: query.trim().length,
    });
    closePopover("selection");
  };

  const handleCreated = (location: IncotermLocationOption, duplicateConfirmed: boolean) => {
    onChange(toSelectedIncotermLocation(location));
    setRecentCache(null);
    captureEvent("incoterm_location_created", {
      context: trackingContext,
      location_id: location.id,
      country_code: location.country_code,
      duplicate_confirmed: duplicateConfirmed,
    });
    closePopover("selection");
  };

  const handleCreateFailed = (reason: "invalid" | "error") =>
    captureEvent("incoterm_location_create_failed", { context: trackingContext, reason });

  const enterCreateMode = () => {
    const q = query.trim();
    setCreatePrefill(!q ? {} : /^[\d\s]+$/.test(q) ? { postal_code: q } : { city: q });
    setMode("create");
  };

  const handleQueryChange = (next: string) => {
    if (!isOpenRef.current && !isDisabled) {
      isOpenRef.current = true;
      setIsOpen(true);
      setMode("search");
    }
    setQuery(next);
    clearDebounce();
    if (next.trim().length < INCOTERM_LOCATION_SEARCH_MIN_CHARS) {
      showRecent();
      return;
    }
    debounceRef.current = setTimeout(() => fetchPage(next.trim(), 0), INCOTERM_LOCATION_SEARCH_DEBOUNCE_MS);
  };

  const moveActiveOption = (direction: 1 | -1) => {
    let index = activeIndex;
    do {
      index += direction;
    } while (index >= 0 && index < results.length && !hasPostalCode(results[index]));
    if (index < 0 || index >= results.length) return;
    setActiveIndex(index);
    document.getElementById(optionId(index))?.scrollIntoView?.({ block: "nearest" });
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isTypingKey = (e.key.length === 1 || e.key === "Backspace") && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (!isOpen && isTypingKey) {
      // Focused but closed (e.g. right after a keyboard selection): start a fresh query instead of editing the label.
      openPopover();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) openPopover();
      else if (mode === "search") moveActiveOption(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter" && isOpen) {
      e.preventDefault(); // keep the focus (TextInput blurs on Enter)
      if (mode === "search" && results[activeIndex])
        selectOption(results[activeIndex], isRecentQuery ? "recent" : "search");
    } else if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      closePopover("dismiss");
    } else if (e.key === "Tab" && isOpen) {
      closePopover("dismiss");
    }
  };

  // Grid cell edit: load the recently used locations right away (the popover starts open).
  useEffect(() => {
    if (initiallyOpen) void startSearch("", 0).then((response) => applySearchResponse(response, "", 0));
  }, [initiallyOpen, startSearch, applySearchResponse]);

  // Drop pending searches on unmount.
  useEffect(() => {
    const requestSeq = requestSeqRef;
    const debounce = debounceRef;
    return () => {
      requestSeq.current += 1;
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, []);

  // Close on an outside mousedown — search mode only, so a half-filled create form is never lost.
  useEffect(() => {
    if (!isOpen || mode !== "search") return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      closePopover("dismiss");
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, mode, closePopover]);

  /** Below the input, or above when it doesn't fit and there is more room above. Applied to the DOM directly. */
  const updatePosition = useCallback((event?: Event) => {
    const input = inputRef.current;
    const popover = popoverRef.current;
    const body = popoverBodyRef.current;
    const content = popoverContentRef.current;
    if (!input || !popover || !body || !content) return;
    if (event?.target instanceof Node && popover.contains(event.target)) return; // scrolling the list itself

    const rect = input.getBoundingClientRect();
    const width = Math.max(rect.width, POPOVER_MIN_WIDTH);
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
    const bodyMaxHeight = parseFloat(getComputedStyle(body).maxHeight);
    const naturalHeight =
      popover.offsetHeight -
      body.offsetHeight +
      Math.min(content.offsetHeight, Number.isNaN(bodyMaxHeight) ? Infinity : bodyMaxHeight);
    const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - POPOVER_GAP - VIEWPORT_MARGIN;
    const openUpward = naturalHeight > spaceBelow && spaceAbove > spaceBelow;

    popover.style.left = `${left}px`;
    popover.style.width = `${width}px`;
    popover.style.top = openUpward ? "" : `${rect.bottom + POPOVER_GAP}px`;
    popover.style.bottom = openUpward ? `${window.innerHeight - rect.top + POPOVER_GAP}px` : "";
    popover.style.maxHeight = `${Math.max(openUpward ? spaceAbove : spaceBelow, 160)}px`;
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const observer = new ResizeObserver(() => updatePosition());
    if (popoverContentRef.current) observer.observe(popoverContentRef.current);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, mode, updatePosition]);

  const handleFocus = () => {
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false;
      return;
    }
    openPopover();
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    closePopover("dismiss");
    focusInput();
  };

  const inputValue = isOpen ? query : value ? value.label || buildIncotermLocationLabel(value) : "";
  const hasMore = !!pageInfo && pageInfo.number + 1 < pageInfo.total_pages;
  const recentSource: SelectionSource = isRecentQuery ? "recent" : "search";

  const searchContent = (
    <>
      {isRecentQuery && results.length > 0 && (
        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500">
          {translate("incoterm-location.recent")}
        </div>
      )}
      <div role="listbox" id={listboxId}>
        {results.map((option, index) => {
          const isOptionDisabled = !hasPostalCode(option);
          const isSelected = !!value?.id && value.id === option.id;
          return (
            <button
              key={option.id}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={isSelected}
              aria-disabled={isOptionDisabled}
              disabled={isOptionDisabled}
              tabIndex={-1}
              onClick={() => selectOption(option, recentSource)}
              onMouseEnter={() => !isOptionDisabled && setActiveIndex(index)}
              className={twMerge(
                "w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors flex items-start gap-2",
                isSelected && "bg-gray-50",
                index === activeIndex && "bg-gray-100",
                isOptionDisabled && "opacity-60 cursor-not-allowed hover:bg-transparent"
              )}
            >
              <span className="flex-1 min-w-0">
                <IncotermLocationOptionContent option={option} />
              </span>
              {isSelected && (
                <Icon name={IconName.Check} color={colors.green[600]} width={20} height={20} className="shrink-0" />
              )}
            </button>
          );
        })}
      </div>
      {loadState === "idle" && results.length === 0 && (
        <div className="px-4 py-5 text-sm text-gray-600">{translate("incoterm-location.no-results")}</div>
      )}
      {loadState === "error" && (
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <span className="text-sm text-red-600">{translate("incoterm-location.load-error")}</span>
          <button
            type="button"
            onClick={() => fetchPage(lastRequestRef.current.q, lastRequestRef.current.page)}
            className="text-sm font-medium text-gray-900 underline"
          >
            {translate("incoterm-location.retry")}
          </button>
        </div>
      )}
      {hasMore && loadState === "idle" && (
        <button
          type="button"
          onClick={() => fetchPage(lastRequestRef.current.q, pageInfo.number + 1)}
          disabled={isLoadingMore}
          className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {translate("incoterm-location.load-more")}
        </button>
      )}
    </>
  );

  return (
    <div ref={containerRef} className={twMerge("relative w-full", className)}>
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-400"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      </div>
      <TextInput
        name={name}
        value={inputValue}
        onChange={(e) => handleQueryChange(e.target.value)}
        onFocus={handleFocus}
        onClick={openPopover}
        onKeyDown={handleInputKeyDown}
        inputRef={inputRef}
        autoFocus={autoFocus}
        placeholder={placeholder ?? translate("incoterm-location.search-placeholder")}
        isDisabled={isDisabled}
        classNames={twMerge("w-full pl-10 pr-9", error && "border-red-500")}
        autoComplete="off"
        ariaProps={{
          role: "combobox",
          "aria-expanded": isOpen,
          "aria-controls": isOpen ? listboxId : undefined,
          "aria-activedescendant": isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined,
          "aria-autocomplete": "list",
        }}
      />
      {loadState === "loading" || isLoadingMore ? (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Icon name={IconName.Loader} size={spacing[4]} color={colors.gray[500]} className="animate-spin" />
        </div>
      ) : (
        allowClear &&
        value &&
        !isDisabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={translate("incoterm-location.clear")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
          >
            <Icon name={IconName.Cancel} size={spacing[4]} colorMode="fill" color={colors.gray[500]} />
          </button>
        )
      )}

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popoverRef}
            data-product-dropdown
            className={twMerge(
              "fixed z-9999 flex flex-col bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden",
              mode === "create" && "min-w-[420px]"
            )}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={handlePopoverKeyDown}
          >
            <div
              ref={popoverBodyRef}
              className={twMerge("flex-1 min-h-0 overflow-y-auto", mode === "search" && "max-h-80")}
            >
              <div ref={popoverContentRef}>
                {mode === "create" ? (
                  <IncotermLocationCreateForm
                    name={name}
                    prefill={createPrefill}
                    onCancel={() => {
                      setMode("search");
                      focusInput();
                    }}
                    onCreated={handleCreated}
                    onCreateFailed={handleCreateFailed}
                    onUseExisting={(match) => selectOption(match, "duplicate_match")}
                  />
                ) : (
                  searchContent
                )}
              </div>
            </div>
            {mode === "search" && (
              <div className="shrink-0 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={enterCreateMode}
                  className="w-full px-4 py-3 text-left text-sm font-semibold text-red-600 hover:bg-gray-50"
                >
                  {translate("incoterm-location.create-new")}
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default IncotermLocationSelect;
